import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/apiClient';
import './GoldCalculatorPage.css';
import '../styles/pro-page.css';

function formatNumber(value, digits = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return number.toLocaleString('zh-CN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatSigned(value, digits = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return `${number >= 0 ? '+' : ''}${formatNumber(number, digits)}`;
}

function formatTime(value) {
  if (!value) return '-';
  return String(value).replace('T', ' ').replace('+08:00', '').slice(0, 16);
}

function GoldCalculatorPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [cached, setCached] = useState(false);
  const [grams, setGrams] = useState('10');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  async function loadGoldPrice(refresh = false) {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const data = await api.getGoldPrice(refresh ? { refresh: 1 } : {});
      setSnapshot(data.snapshot);
      setCached(Boolean(data.cached));
    } catch (err) {
      setError(err.message || '黄金报价暂时不可用');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadGoldPrice(false);
  }, []);

  const gramAmount = Number(grams);
  const totals = useMemo(() => {
    if (!snapshot || !Number.isFinite(gramAmount)) {
      return {
        international: null,
        domestic: null,
        spread: null,
      };
    }
    return {
      international: snapshot.international.cnyPerGram * gramAmount,
      domestic: snapshot.domestic.priceCnyPerGram * gramAmount,
      spread: snapshot.spread.cnyPerGram * gramAmount,
    };
  }, [snapshot, gramAmount]);

  return (
    <main className="pro-page gold-page">
      <div className="pro-page-inner">
        <header className="pro-hero gold-hero">
          <p className="pro-hero-label">Gold Calculator</p>
          <h1>黄金内外盘换算器</h1>
          <span className="pro-hero-desc">用今日基准价统一换算国际现货黄金与国内账户黄金，快速看清每克价差和溢价率。</span>
        </header>

        {error && <div className="gold-error">{error}</div>}
        {loading && <div className="gold-loading">正在读取今日黄金基准价...</div>}

        {snapshot && (
          <>
            <section className="gold-benchmark">
              <div className="gold-benchmark-head">
                <div>
                  <span>今日基准</span>
                  <strong>{snapshot.snapshotDate}</strong>
                </div>
                <button type="button" onClick={() => loadGoldPrice(true)} disabled={refreshing}>
                  {refreshing ? '刷新中...' : '刷新实时参考价'}
                </button>
              </div>
              <div className="gold-meta">
                <span>{cached ? '使用今日缓存' : snapshot.sourceMode === 'realtime' ? '实时参考' : '新生成今日基准'}</span>
                <span>数据源：金十 MCP</span>
                <span>更新时间：{formatTime(snapshot.capturedAt)}</span>
              </div>
            </section>

            <section className="gold-price-grid" aria-label="黄金报价概览">
              <article className="gold-price-card primary">
                <span>国际折算价</span>
                <strong>{formatNumber(snapshot.international.cnyPerGram)}</strong>
                <em>元/克</em>
                <p>XAUUSD × USDCNH ÷ {snapshot.unit.troyOunceGrams}</p>
              </article>
              <article className="gold-price-card">
                <span>国内账户黄金</span>
                <strong>{formatNumber(snapshot.domestic.priceCnyPerGram)}</strong>
                <em>元/克</em>
                <p>{snapshot.domestic.primaryName || snapshot.domestic.primaryCode}</p>
              </article>
              <article className={snapshot.spread.cnyPerGram >= 0 ? 'gold-price-card spread up' : 'gold-price-card spread down'}>
                <span>国内溢价</span>
                <strong>{formatSigned(snapshot.spread.cnyPerGram)}</strong>
                <em>元/克</em>
                <p>{formatSigned(snapshot.spread.percent, 3)}%</p>
              </article>
            </section>

            <section className="gold-calculator-shell">
              <div className="gold-input-panel">
                <label htmlFor="gold-grams">克数</label>
                <div className="gold-input-row">
                  <input
                    id="gold-grams"
                    value={grams}
                    onChange={(event) => setGrams(event.target.value)}
                    inputMode="decimal"
                    aria-label="克数"
                  />
                  <span>g</span>
                </div>
                <div className="gold-quick-row">
                  {[1, 10, 50, 100, 1000].map((value) => (
                    <button key={value} type="button" onClick={() => setGrams(String(value))}>
                      {value}g
                    </button>
                  ))}
                </div>
              </div>

              <div className="gold-result-panel">
                <div>
                  <span>国际折算总价</span>
                  <strong>{formatNumber(totals.international)}</strong>
                </div>
                <div>
                  <span>国内账户总价</span>
                  <strong>{formatNumber(totals.domestic)}</strong>
                </div>
                <div className={totals.spread >= 0 ? 'up' : 'down'}>
                  <span>总价差</span>
                  <strong>{formatSigned(totals.spread)}</strong>
                </div>
              </div>
            </section>

            <section className="gold-detail-grid">
              <article>
                <h2>国际口径</h2>
                <dl>
                  <span><dt>XAUUSD</dt><dd>{formatNumber(snapshot.international.usdPerOunce)} 美元/盎司</dd></span>
                  <span><dt>美元/克</dt><dd>{formatNumber(snapshot.international.usdPerGram, 4)}</dd></span>
                  <span><dt>USDCNH</dt><dd>{formatNumber(snapshot.quotes.usdCnh.price, 5)}</dd></span>
                </dl>
              </article>
              <article>
                <h2>国内口径</h2>
                <dl>
                  <span><dt>工行账户金</dt><dd>{formatNumber(snapshot.domestic.icbcCnyPerGram)} 元/克</dd></span>
                  <span><dt>农行账户金</dt><dd>{formatNumber(snapshot.domestic.abcCnyPerGram)} 元/克</dd></span>
                  <span><dt>报价时间</dt><dd>{formatTime(snapshot.quotes.xauUsd.time)}</dd></span>
                </dl>
              </article>
            </section>

            <p className="gold-disclaimer">说明：本工具用于市场换算参考，不包含品牌金饰工费、买卖点差、税费和平台手续费。</p>
          </>
        )}
      </div>
    </main>
  );
}

export default GoldCalculatorPage;
